import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { PromptPayQR, usePromptPayConfig } from "../utils/promptpay";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import Toast from "../components/Toast";
import API from "../config";

function AvatarWithFallback({ src, role, imgClass, phClass }) {
  const [imgError, setImgError] = useState(false);
  if (src && !imgError) {
    return <img className={imgClass} src={src} alt="" onError={() => setImgError(true)} />;
  }
  return (
    <div className={`${phClass} ${role}`}>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "55%", height: "55%" }}>
        <circle cx="12" cy="8" r="4" fill="currentColor" opacity="0.9"/>
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="currentColor" opacity="0.9"/>
      </svg>
    </div>
  );
}

function CheckOut() {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const autoSelectId = location.state?.bookingId ?? null;
  const { lang } = useLanguage(); // eslint-disable-line no-unused-vars
  const promptpayConfig = usePromptPayConfig();

  const [bookings,   setBookings]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [toasting,   setToasting]   = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [sending,    setSending]    = useState(false);
  const [extraItems, setExtraItems] = useState([]);
  const [cashPaid,   setCashPaid]   = useState(false);
  const [showPay,    setShowPay]    = useState(false);
  const [payMethod,  setPayMethod]  = useState("cash");
  const [paidMethod, setPaidMethod] = useState("");   // วิธีที่รับชำระไปแล้ว (สำหรับแสดง/ย้อนกลับ)
  const [viewTab,    setViewTab]    = useState("all"); // มุมมอง ออกวันนี้ / ทั้งหมด

  /* popup เพิ่มรายการ (สินค้า/บริการที่บันทึกไว้ + กรอกเอง) */
  const [serviceItems, setServiceItems] = useState([]);
  const [showAddItem,  setShowAddItem]  = useState(false);
  const [itemSearch,   setItemSearch]   = useState("");
  const [customName,   setCustomName]   = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [customQty,    setCustomQty]    = useState(1);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setBookings(
        Array.isArray(data)
          ? data
              .filter(b => b.status === "CheckedIn")
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          : []
      );
    } catch {
      toast(t(lang, "coToastLoadFail"), "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useEffect(() => {
    if (autoSelectId && bookings.length > 0) {
      const found = bookings.find(b => b._id === autoSelectId);
      if (found) openDetail(found);
    }
  }, [autoSelectId, bookings]);

  useEffect(() => {
    fetch(`${API}/api/service-items`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setServiceItems(d); }).catch(() => {});
  }, [token]);

  function toast(msg, type = "success") {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 3000);
  }

  function getGuestName(b) {
    return `${b?.user?.firstname ?? ""} ${b?.user?.lastname ?? ""}`.trim() || b?.guestName || t(lang, "ciUnknownName");
  }

  function getAvatarRole(user) {
    return (user?.role === "admin" || user?.role === "staff") ? "staff" : "guest";
  }

  function openDetail(booking) {
    setExtraItems([]);
    setCashPaid(false);
    setShowPay(false);
    setPayMethod("cash");
    setPaidMethod("");
    setSelected(booking);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function removeItem(id) {
    setExtraItems(prev => prev.filter(i => i.id !== id));
  }

  function addItem() {
    setItemSearch("");
    setCustomName("");
    setCustomAmount("");
    setCustomQty(1);
    setShowAddItem(true);
  }

  function addServiceItem(s) {
    const unitPrice = Number(s.price);
    setExtraItems(prev => [...prev, { id: Date.now(), name: s.name, qty: 1, unitPrice, amount: unitPrice }]);
    setShowAddItem(false);
  }

  function addCustomItem() {
    const unitPrice = parseFloat(customAmount);
    const qty = Math.max(1, Number(customQty) || 1);
    if (!customName.trim() || isNaN(unitPrice) || unitPrice < 0) return;
    setExtraItems(prev => [...prev, { id: Date.now(), name: customName.trim(), qty, unitPrice, amount: qty * unitPrice }]);
    setShowAddItem(false);
  }

  function updateItemQty(id, qtyRaw) {
    const qty = Math.max(1, Number(qtyRaw) || 1);
    setExtraItems(prev => prev.map(i =>
      i.id === id ? { ...i, qty, amount: qty * (i.unitPrice ?? i.amount) } : i
    ));
  }

  const roomBalance = selected ? Math.max((selected.totalAmount ?? 0) - (selected.paidAmount ?? 0), 0) : 0;
  const roomPaid    = roomBalance === 0;

  const extraTotal = extraItems.reduce((s, i) => s + i.amount, 0);
  const totalAll   = roomBalance + extraTotal;

  function confirmPay() {
    if (totalAll === 0) return;
    setCashPaid(true);
    setPaidMethod(payMethod);
    setShowPay(false);
    toast(`${payMethod === "cash" ? t(lang,"coToastPayCash") : t(lang,"coToastPayQR")} ${fmt(totalAll)} ${t(lang,"coToastPaySuffix")}`);
  }

  function undoPay() {
    setCashPaid(false);
    setPaidMethod("");
  }

  const canConfirm = cashPaid || (roomPaid && extraTotal === 0);

  async function handleCheckOut() {
    if (!selected) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/checkout/${selected._id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cashPaid, extraItems }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || t(lang, "coToastCheckoutFail"));
      }

      toast(`${getGuestName(selected)} ${t(lang,"coToastCheckoutOk")}`);
      setSelected(null);
      fetchBookings();
    } catch (err) {
      toast(`${t(lang,"coToastCheckoutFail")}: ${err.message}`, "error");
    } finally {
      setSending(false);
    }
  }

  const isSameDay = (d, ref) => {
    if (!d) return false;
    const x = new Date(d);
    return x.getFullYear() === ref.getFullYear() && x.getMonth() === ref.getMonth() && x.getDate() === ref.getDate();
  };
  const todayRef   = new Date();
  const todayCount = bookings.filter(b => isSameDay(b.checkOut, todayRef)).length;

  const filtered = bookings.filter(b => {
    if (viewTab === "today" && !isSameDay(b.checkOut, todayRef)) return false;
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const name = getGuestName(b).toLowerCase();
    return name.includes(q) || (b.roomUnit?.roomNumber ?? "").toLowerCase().includes(q);
  });

  const fmtDate = d =>
    d ? new Date(d).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Bangkok" }) : "—";
  const fmtDateTime = d =>
    d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Bangkok" }) : "—";
  const fmt = n => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 });

  const toAvatarSrc = (av) => !av ? null : av.startsWith('http') ? av : `/uploads/${av}`;
  const avatarSrc = toAvatarSrc(selected?.user?.avatar);

  return (
    <>
      <style>{`
        .co-page{min-height:100vh;background:#f5f5f5;font-family:'Noto Sans Thai',sans-serif}
        .co-wrap{max-width:1100px;margin:0 auto;padding:32px 24px 60px}

        .co-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;flex-wrap:wrap;gap:12px}
        .co-title{display:flex;align-items:center;gap:8px;font-size:20px;font-weight:700;color:#1a1a1a}
        .co-search-wrap{position:relative}
        .co-search{padding:9px 14px 9px 36px;border-radius:8px;border:1px solid #e0e0e0;font-size:13px;width:240px;outline:none;background:#fff}
        .co-search::placeholder{color:#bbb}
        .co-search:focus{border-color:#aaa}
        .co-search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:#bbb;pointer-events:none}
        .co-tabs{display:inline-flex;background:#eef0f2;border-radius:9px;padding:3px;gap:2px}
        .co-tab{border:none;background:none;padding:7px 14px;border-radius:7px;font-size:12.5px;font-weight:600;color:#6b7280;cursor:pointer;font-family:'Noto Sans Thai',sans-serif;white-space:nowrap;transition:background .15s,color .15s}
        .co-tab.active{background:#fff;color:#1a1a1a;box-shadow:0 1px 3px rgba(0,0,0,.1)}

        .co-list{display:flex;flex-direction:column;gap:12px}
        .co-card{display:flex;align-items:center;gap:18px;padding:18px 24px;background:#fff;border:1px solid #e0e0e0;border-radius:14px;transition:border-color .15s}
        .co-card:hover{border-color:#c0c0c0}
        .co-avatar{width:88px;height:88px;border-radius:12px;object-fit:cover;flex-shrink:0}

        /* Avatar placeholder list: แยก guest (เทา) / staff (น้ำเงิน) */
        .co-avatar-ph{
          width:88px;height:88px;border-radius:12px;
          display:flex;align-items:center;justify-content:center;
          font-size:28px;font-weight:700;flex-shrink:0;
        }
        .co-avatar-ph.guest{background:linear-gradient(135deg,#e5e7eb,#d1d5db);color:#4b5563}
        .co-avatar-ph.staff{background:linear-gradient(135deg,#dbeafe,#bfdbfe);color:#1d4ed8}

        .co-info{flex:1;min-width:0}
        .co-name{font-size:15px;font-weight:700;color:#1a1a1a;margin-bottom:10px}
        .co-meta{display:flex;flex-direction:column;gap:5px}
        .co-meta-item{display:flex;align-items:center;gap:6px;font-size:12.5px;color:#6b7280}
        .co-right{display:flex;align-items:center;gap:24px;flex-shrink:0}
        .co-guest-label{font-size:12px;color:#9ca3af;margin-bottom:2px;text-align:right}
        .co-guest-val{font-size:13.5px;font-weight:700;color:#1a1a1a;text-align:right}
        .co-btn-checkout{display:inline-flex;align-items:center;gap:6px;padding:10px 26px;background:#1a1a1a;color:#fff;border:none;border-radius:999px;font-size:14px;font-weight:600;cursor:pointer;transition:background .15s;font-family:'Noto Sans Thai',sans-serif;white-space:nowrap}
        .co-btn-checkout:hover{background:#333}
        .co-late-tag{font-size:10px;font-weight:700;margin-left:4px;background:#fef2f2;color:#dc2626;padding:1px 5px;border-radius:4px;border:1px solid #fecaca}

        .co-empty{text-align:center;padding:60px 20px;color:#9ca3af;background:#fff;border-radius:14px;border:1px solid #e0e0e0}
        .co-loading{text-align:center;padding:60px 20px;color:#9ca3af;font-size:14px;background:#fff;border-radius:14px;border:1px solid #e0e0e0}
        @keyframes co-spin{to{transform:rotate(360deg)}}
        .co-spinner{width:28px;height:28px;border:3px solid #e5e7eb;border-top-color:#1a1a1a;border-radius:50%;animation:co-spin .7s linear infinite;margin:0 auto 12px}

        /* Detail */
        .co-detail-page{background:#fff;border-radius:16px;border:1px solid #e0e0e0;padding:32px 40px}
        .co-detail-top{display:flex;align-items:center;gap:14px;margin-bottom:28px}
        .co-back-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 16px;border-radius:8px;border:1px solid #e0e0e0;background:#fff;color:#374151;font-size:13px;font-weight:500;cursor:pointer;font-family:'Noto Sans Thai',sans-serif}
        .co-back-btn:hover{background:#f3f4f6}
        .co-detail-title{font-size:20px;font-weight:700;color:#1a1a1a;display:flex;align-items:center;gap:8px}
        .co-detail-body{display:grid;grid-template-columns:1fr 360px;gap:36px}

        .co-guest-row{display:flex;align-items:center;gap:14px;margin-bottom:28px}
        .co-det-avatar{width:68px;height:68px;border-radius:50%;object-fit:cover;flex-shrink:0}

        /* Detail avatar placeholder: แยก guest / staff */
        .co-det-avatar-ph{
          width:68px;height:68px;border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          font-size:24px;font-weight:700;flex-shrink:0;
        }
        .co-det-avatar-ph.guest{background:linear-gradient(135deg,#e5e7eb,#d1d5db);color:#4b5563}
        .co-det-avatar-ph.staff{background:linear-gradient(135deg,#dbeafe,#bfdbfe);color:#1d4ed8}

        .co-guest-name{font-size:16px;font-weight:700;color:#1a1a1a}
        .co-guest-email{font-size:12.5px;color:#6b7280;margin-top:2px}
        .co-guest-age{font-size:12.5px;color:#6b7280}

        .co-pay-label{font-size:14px;font-weight:700;color:#1a1a1a;margin-bottom:11px}
        .co-pay-list{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}

        .co-row-room{background:#111;border-radius:9px;display:flex;align-items:center;justify-content:space-between;padding:12px 16px;color:#fff}
        .co-row-room-paid{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:9px;display:flex;align-items:center;justify-content:space-between;padding:12px 16px}
        .co-lock-label{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:500}
        .co-paid-badge{display:flex;align-items:center;gap:5px;font-size:12px;font-weight:700;color:#16a34a}

        .co-pay-row{background:#111;border-radius:9px;display:flex;align-items:center;justify-content:space-between;padding:12px 16px;color:#fff}
        .co-pay-name{font-size:13px;font-weight:500}
        .co-pay-right{display:flex;align-items:center;gap:10px}
        .co-pay-amount{font-size:13.5px;font-weight:600}
        .co-del-btn{width:26px;height:26px;background:#dc2626;border:none;border-radius:6px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0}
        .co-add-btn{background:#111;color:#fff;border:none;border-radius:8px;padding:9px 17px;font-size:13px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-family:'Noto Sans Thai',sans-serif}
        .co-add-btn:hover{background:#333}

        /* Right panel */
        .co-detail-right{background:#f9f9f9;border-radius:16px;border:1px solid #e8e8e8;padding:24px;display:flex;flex-direction:column;gap:14px}
        .co-sum-title{font-size:15px;font-weight:700;color:#1a1a1a}
        .co-room-img{width:100%;height:160px;object-fit:cover;border-radius:12px}
        .co-room-img-ph{width:100%;height:160px;border-radius:12px;background:#e5e7eb;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:13px}
        .co-sum-roomname{font-size:17px;font-weight:700;color:#1a1a1a;margin-top:4px}
        .co-sum-meta{display:flex;flex-direction:column;gap:7px}
        .co-sum-item{display:flex;align-items:center;gap:7px;font-size:12.5px;color:#6b7280}
        .co-sum-divider{border:none;border-top:1px solid #e0e0e0;margin:2px 0}

        .co-total-row{display:flex;align-items:center;justify-content:space-between;gap:8px}
        .co-total-label{font-size:13px;color:#374151;margin-bottom:3px}
        .co-total-amount{font-size:26px;font-weight:800;color:#1a1a1a;line-height:1.1}
        .co-total-amount.paid{color:#16a34a}

        .co-cash-btn{display:inline-flex;align-items:center;gap:6px;padding:10px 16px;background:#16a34a;color:#fff;border:none;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Noto Sans Thai',sans-serif;transition:background .15s;white-space:nowrap;flex-shrink:0}
        .co-cash-btn:hover{background:#15803d}
        .co-cash-done{display:inline-flex;align-items:center;gap:5px;padding:8px 13px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;font-size:12px;font-weight:700;color:#16a34a;white-space:nowrap;flex-shrink:0}

        /* Price breakdown — เหมือนหน้าเช็คอิน */
        .co-price-block{display:flex;flex-direction:column;gap:9px}
        .co-price-row{display:flex;justify-content:space-between;align-items:center}
        .co-price-label{font-size:13px;color:#6b7280}
        .co-price-val{font-size:13.5px;font-weight:600;color:#1a1a1a}
        .co-price-val.paid{color:#16a34a}
        .co-price-divider{border-top:1px dashed #d1d5db;margin:2px 0}
        .co-balance-label{font-size:14px;font-weight:700;color:#1a1a1a}
        .co-balance-val{font-size:20px;font-weight:800}
        .co-balance-val.due{color:#dc2626}
        .co-balance-val.paid-full{font-size:14px;color:#16a34a;font-weight:700}
        .co-paybig-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;width:100%;padding:11px;background:#16a34a;color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Noto Sans Thai',sans-serif;transition:background .15s}
        .co-paybig-btn:hover{background:#15803d}
        .co-paid-note{display:flex;align-items:center;justify-content:space-between;gap:8px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:9px;padding:8px 12px;font-size:12px;color:#166534;font-weight:500}

        .co-undo-btn{background:#fff;border:1px solid #d1d5db;border-radius:7px;padding:4px 12px;font-size:12px;color:#374151;font-weight:500;cursor:pointer;font-family:'Noto Sans Thai',sans-serif}
        .co-undo-btn:hover{background:#f3f4f6}

        /* Payment popup */
        .co-pay-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:2000;display:flex;align-items:center;justify-content:center;animation:co-fadein .15s ease}
        .co-pay-modal{background:#fff;border-radius:18px;width:92vw;max-width:400px;padding:26px 28px;box-shadow:0 12px 40px rgba(0,0,0,.2)}
        .co-pay-title{font-size:17px;font-weight:700;color:#1a1a1a;margin-bottom:4px;display:flex;align-items:center;gap:8px}
        .co-pay-sub{font-size:12.5px;color:#6b7280;margin-bottom:18px}
        .co-pay-amount-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;padding:16px;text-align:center;margin-bottom:18px}
        .co-pay-amount-label{font-size:12px;color:#6b7280;margin-bottom:4px;display:flex;align-items:center;justify-content:center;gap:5px}
        .co-pay-amount-val{font-size:28px;font-weight:800;color:#1a1a1a}
        .co-pay-methods{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px}
        .co-pay-method{display:flex;flex-direction:column;align-items:center;gap:8px;padding:16px 10px;border-radius:12px;border:2px solid #e5e7eb;background:#fff;cursor:pointer;font-size:13.5px;font-weight:600;color:#374151;font-family:'Noto Sans Thai',sans-serif;transition:border-color .15s,background .15s}
        .co-pay-method.active{border-color:#16a34a;background:#f0fdf4;color:#166534}
        .co-pay-qrbox{background:#f9fafb;border:1px dashed #d1d5db;border-radius:12px;padding:18px;display:flex;flex-direction:column;align-items:center;gap:8px;margin-bottom:18px;color:#6b7280;font-size:12.5px}
        .co-pay-actions{display:flex;gap:10px}
        .co-pay-cancel{flex:1;padding:12px;border-radius:999px;border:1px solid #d1d5db;background:#fff;color:#374151;font-size:14px;font-weight:600;cursor:pointer;font-family:'Noto Sans Thai',sans-serif}
        .co-pay-cancel:hover{background:#f3f4f6}
        .co-pay-ok{flex:1.4;padding:12px;border-radius:999px;border:none;background:#16a34a;color:#fff;font-size:14px;font-weight:700;cursor:pointer;font-family:'Noto Sans Thai',sans-serif;transition:background .15s}
        .co-pay-ok:hover{background:#15803d}

        .co-confirm-btn{width:100%;padding:14px;background:#1a1a1a;color:#fff;border:none;border-radius:999px;font-size:15px;font-weight:700;cursor:pointer;font-family:'Noto Sans Thai',sans-serif;margin-top:4px;transition:background .15s}
        .co-confirm-btn:hover:not(:disabled){background:#333}
        .co-confirm-btn:disabled{background:#d1d5db;color:#9ca3af;cursor:not-allowed}
        .co-confirm-hint{font-size:12px;color:#ef4444;text-align:center;margin-top:-6px}

        @keyframes co-fadein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .co-toast{position:fixed;top:76px;right:28px;padding:12px 20px;border-radius:10px;font-size:13.5px;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,.15);z-index:3000;animation:co-fadein .2s ease;display:flex;align-items:center;gap:8px;max-width:340px}
        .co-toast.success{background:#f0fdf4;color:#166534;border:1px solid #bbf7d0}
        .co-toast.error{background:#fef2f2;color:#991b1b;border:1px solid #fecaca}

        @media(max-width:820px){.co-detail-body{grid-template-columns:1fr}}
      `}</style>

      <div className="co-page">
        <div className="co-wrap">

          {selected ? (
            <div className="co-detail-page">
              <div className="co-detail-top">
                <button className="co-back-btn" onClick={() => setSelected(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                  </svg>
                  {t(lang, "coBack")}
                </button>
                <div className="co-detail-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  {t(lang, "coTitle")}
                </div>
              </div>

              <div className="co-detail-body">

                {/* LEFT */}
                <div>
                  <div className="co-guest-row">
                    <AvatarWithFallback
                      src={avatarSrc}
                      role={getAvatarRole(selected?.user)}
                      imgClass="co-det-avatar"
                      phClass="co-det-avatar-ph"
                    />
                    <div>
                      <div className="co-guest-name">{getGuestName(selected)}</div>
                      {selected.user?.email && <div className="co-guest-email">{selected.user.email}</div>}
                      {selected.user?.age   && <div className="co-guest-age">{t(lang, "coAge")} {selected.user.age}</div>}
                    </div>
                  </div>

                  <div className="co-pay-label">{t(lang, "coPayment")}</div>
                  <div className="co-pay-list">

                    {/* ราคาห้อง */}
                    {roomPaid ? (
                      <div className="co-row-room-paid">
                        <div className="co-lock-label" style={{ color: "#166534" }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                          {t(lang, "coRoomPrice")}
                        </div>
                        <div className="co-paid-badge">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          {t(lang, "coRoomPaid")}
                        </div>
                      </div>
                    ) : (
                      <div className="co-row-room">
                        <div className="co-lock-label" style={{ color: "#fff" }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                          {t(lang, "coRoomPrice")}
                        </div>
                        <span style={{ fontSize: "13.5px", fontWeight: 600 }}>{fmt(roomBalance)} THB</span>
                      </div>
                    )}

                    {/* รายการเพิ่มเติม */}
                    {extraItems.map(item => (
                      <div className="co-pay-row" key={item.id}>
                        <span className="co-pay-name">{item.name}</span>
                        <div className="co-pay-right">
                          <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"#d1d5db" }}>
                            {t(lang, "coQty")}
                            <input type="number" min={1} value={item.qty ?? 1}
                              onChange={e => updateItemQty(item.id, e.target.value)}
                              style={{ width:52, padding:"4px 6px", borderRadius:6, border:"none",
                                       fontSize:12.5, textAlign:"center", fontFamily:"inherit", outline:"none" }} />
                          </span>
                          <span className="co-pay-amount">{fmt(item.amount)} THB</span>
                          <button className="co-del-btn" onClick={() => removeItem(item.id)}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button className="co-add-btn" onClick={addItem}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    {t(lang, "coAddItem")}
                  </button>
                </div>

                {/* RIGHT */}
                <div className="co-detail-right">
                  <div className="co-sum-title">{t(lang, "coBookingDetail")}</div>

                  {(() => {
                    const img = selected.roomType?.images?.[0] ?? selected.roomType?.image ?? selected.roomUnit?.image ?? null;
                    return img
                      ? <img className="co-room-img" src={`/uploads/${img}`} alt="" />
                      : <div className="co-room-img-ph">{t(lang, "coNoImage")}</div>;
                  })()}

                  <div className="co-sum-roomname">
                    {selected.roomType?.name ?? t(lang, "ciDefaultRoom")} : {selected.roomUnit?.roomNumber ?? "—"}
                  </div>

                  <div className="co-sum-meta">
                    <div className="co-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      {t(lang, "coGuests")} : {selected.guests ?? selected.roomType?.adults ?? "—"}
                    </div>
                    <div className="co-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      {t(lang, "coBookedOn")} : {fmtDateTime(selected.createdAt)}
                    </div>
                    <div className="co-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                      </svg>
                      {t(lang, "coCheckIn")} : {fmtDate(selected.checkIn)}
                    </div>
                    <div className="co-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                      </svg>
                      {t(lang, "coCheckOut")} : {fmtDate(selected.checkOut)}
                    </div>
                  </div>

                  <hr className="co-sum-divider" />

                  {/* Price breakdown — UI เดียวกับหน้าเช็คอิน */}
                  <div className="co-price-block">
                    <div className="co-price-row">
                      <span className="co-price-label">{t(lang, "coTotalPrice")}</span>
                      <span className="co-price-val">{fmt(selected.totalAmount)} THB</span>
                    </div>
                    <div className="co-price-row">
                      <span className="co-price-label">{t(lang, "coPaidLabel")}</span>
                      <span className="co-price-val paid">{fmt(selected.paidAmount)} THB</span>
                    </div>
                    {extraTotal > 0 && (
                      <div className="co-price-row">
                        <span className="co-price-label">{t(lang, "coExtraItems")}</span>
                        <span className="co-price-val">{fmt(extraTotal)} THB</span>
                      </div>
                    )}
                    <div className="co-price-divider" />
                    {!cashPaid && totalAll > 0 ? (
                      <>
                        <div className="co-price-row">
                          <span className="co-balance-label">{t(lang, "coBalanceDue")}</span>
                          <span className="co-balance-val due">{fmt(totalAll)} THB</span>
                        </div>
                        <button className="co-paybig-btn" onClick={() => { setPayMethod("cash"); setShowPay(true); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/>
                            <path d="M6 12h.01M18 12h.01"/>
                          </svg>
                          {t(lang, "coPayBtn")}
                        </button>
                      </>
                    ) : (
                      <div className="co-price-row">
                        <span className="co-balance-label">{t(lang, "coBalancePaid")}</span>
                        <span className="co-balance-val paid-full">{t(lang, "coPaidFull")}</span>
                      </div>
                    )}
                    {cashPaid && (
                      <div className="co-paid-note">
                        <span>{paidMethod === "qr" ? t(lang, "coReceivedQR") : t(lang, "coReceivedCash")} {fmt(totalAll)} THB</span>
                        <button className="co-undo-btn" onClick={undoPay}>{t(lang, "coUndoBtn")}</button>
                      </div>
                    )}
                  </div>

                  <button
                    className="co-confirm-btn"
                    disabled={sending || !canConfirm}
                    onClick={handleCheckOut}
                  >
                    {sending ? t(lang, "coConfirming") : t(lang, "coConfirm")}
                  </button>

                  {!canConfirm && (
                    <div className="co-confirm-hint">⚠️ {t(lang, "coPayWarn")}</div>
                  )}
                </div>

              </div>
            </div>

          ) : (
            <>
              <div className="co-header">
                <div className="co-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  {t(lang, "coTitle")}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                  <div className="co-tabs">
                    <button className={`co-tab${viewTab === "all" ? " active" : ""}`} onClick={() => setViewTab("all")}>{t(lang, "coTabAll")}</button>
                    <button className={`co-tab${viewTab === "today" ? " active" : ""}`} onClick={() => setViewTab("today")}>
                      {t(lang, "coTabToday")}{todayCount > 0 ? ` (${todayCount})` : ""}
                    </button>
                  </div>
                  <div className="co-search-wrap">
                    <svg className="co-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input className="co-search" placeholder={t(lang, "coSearchPh")} value={search}
                      onChange={e => setSearch(e.target.value)} />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="co-loading"><div className="co-spinner"/>{t(lang, "coLoadingData")}</div>
              ) : filtered.length === 0 ? (
                <div className="co-empty">
                  <div style={{ fontSize: 40, marginBottom: 10 }}>🏨</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{t(lang, "coNoList")}</div>
                </div>
              ) : (
                <div className="co-list">
                  {filtered.map(b => {
                    const name   = getGuestName(b);
                    const src    = toAvatarSrc(b.user?.avatar);
                    const isLate = b.checkOut && new Date(b.checkOut) < new Date();
                    return (
                      <div className="co-card" key={b._id}>
                        <AvatarWithFallback
                          src={src}
                          role={getAvatarRole(b.user)}
                          imgClass="co-avatar"
                          phClass="co-avatar-ph"
                        />
                        <div className="co-info">
                          <div className="co-name">{name}</div>
                          <div className="co-meta">
                            <div className="co-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                              </svg>
                              {t(lang, "coGuests")} : {b.roomType?.adults ?? 1}
                            </div>
                            <div className="co-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                              </svg>
                              {t(lang, "coBookedOn")} : {fmtDateTime(b.createdAt)}
                            </div>
                            <div className="co-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                              </svg>
                              {t(lang, "coCheckIn")} : {fmtDate(b.checkIn)}
                            </div>
                            <div className="co-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                              </svg>
                              {t(lang, "coCheckOut")} : {fmtDate(b.checkOut)}
                              {isLate && <span className="co-late-tag">{t(lang, "coLate")}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="co-right">
                          <div>
                            <div className="co-guest-label">{t(lang, "coCustomer")} :</div>
                            <div className="co-guest-val">{name}</div>
                          </div>
                          <button className="co-btn-checkout" onClick={() => openDetail(b)}>
                            {t(lang, "coCheckoutBtn")}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Add item popup */}
      {showAddItem && (
        <div className="co-pay-overlay" onClick={e => { if (e.target === e.currentTarget) setShowAddItem(false); }}>
          <div className="co-pay-modal" style={{ maxWidth: 680, display:"flex", flexDirection:"column", maxHeight:"88vh" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div className="co-pay-title">{t(lang, "coAddItem")}</div>
                <div className="co-pay-sub">{t(lang, "coAddItemSub")}</div>
              </div>
              <button onClick={() => setShowAddItem(false)}
                style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:20, lineHeight:1 }}>✕</button>
            </div>

            <div style={{ flex:1, overflowY:"auto" }}>
              {/* ฟอร์มกรอกเอง — การ์ดด้านบน */}
              <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:12, padding:"14px 16px", marginBottom:18 }}>
                <p style={{ margin:"0 0 12px", fontSize:13, fontWeight:600, color:"#111827" }}>{t(lang, "coCustomEntry")}</p>
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:10, marginBottom:12 }}>
                  <div>
                    <label style={{ fontSize:12, color:"#374151", display:"block", marginBottom:4 }}>{t(lang, "coItemName")}</label>
                    <input autoFocus value={customName} onChange={e => setCustomName(e.target.value)} placeholder={t(lang, "coItemNamePh")}
                      style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid #d1d5db",
                               fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }} />
                  </div>
                  <div>
                    <label style={{ fontSize:12, color:"#374151", display:"block", marginBottom:4 }}>{t(lang, "coQty")}</label>
                    <input type="number" min={1} value={customQty} onChange={e => setCustomQty(e.target.value)}
                      style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid #d1d5db",
                               fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit", textAlign:"center" }} />
                  </div>
                  <div>
                    <label style={{ fontSize:12, color:"#374151", display:"block", marginBottom:4 }}>{t(lang, "coUnitPrice")}</label>
                    <input type="number" min={0} value={customAmount} onChange={e => setCustomAmount(e.target.value)} placeholder="0.00"
                      style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid #d1d5db",
                               fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit", textAlign:"right" }} />
                  </div>
                </div>
                <button
                  style={{ width:"100%", padding:"10px", borderRadius:8, border:"none", background:"#1a1a1a",
                           color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
                           opacity: (!customName.trim() || customAmount === "") ? 0.5 : 1 }}
                  disabled={!customName.trim() || customAmount === ""}
                  onClick={addCustomItem}>
                  {t(lang, "coAddBtn")}
                </button>
              </div>

              {/* รายการสินค้า/บริการ — อยู่ล่าง พร้อมค้นหา */}
              <p style={{ margin:"0 0 8px", fontSize:13, fontWeight:600, color:"#111827" }}>{t(lang, "coServiceItems")}</p>
              <div style={{ position:"relative", marginBottom:10 }}>
                <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"#bbb", fontSize:13 }}>🔍</span>
                <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder={t(lang, "coSearchItem")}
                  style={{ width:"100%", padding:"9px 12px 9px 34px", borderRadius:8, border:"1px solid #d1d5db",
                           fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }} />
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {serviceItems.filter(s => !itemSearch.trim() || s.name.toLowerCase().includes(itemSearch.toLowerCase())).length === 0 && (
                  <p style={{ textAlign:"center", color:"#9ca3af", fontSize:13, padding:"16px 0", margin:0 }}>
                    {serviceItems.length === 0 ? t(lang, "coNoSavedItems") : t(lang, "coNoSearchItem")}
                  </p>
                )}
                {serviceItems
                  .filter(s => !itemSearch.trim() || s.name.toLowerCase().includes(itemSearch.toLowerCase()))
                  .map(s => (
                    <div key={s._id} onClick={() => addServiceItem(s)}
                      style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                               padding:"11px 14px", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background="#f0f9ff"}
                      onMouseLeave={e => e.currentTarget.style.background="#fff"}>
                      <span style={{ fontSize:13, fontWeight:500, color:"#111827" }}>{s.name}</span>
                      <span style={{ fontSize:13, fontWeight:600, color:"#111827", whiteSpace:"nowrap" }}>{fmt(s.price)} THB</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment popup */}
      {showPay && selected && (
        <div className="co-pay-overlay" onClick={e => { if (e.target === e.currentTarget) setShowPay(false); }}>
          <div className="co-pay-modal">
            <div className="co-pay-title">{t(lang, "coPayTitle")}</div>
            <div className="co-pay-sub">{getGuestName(selected)} · {t(lang,"ciRoomLabel")} {selected.roomUnit?.roomNumber ?? "—"}</div>

            {/* ยอดเงิน (ล็อค แก้ไขไม่ได้) */}
            <div className="co-pay-amount-box">
              <div className="co-pay-amount-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                {t(lang, "coAmountDue")}
              </div>
              <div className="co-pay-amount-val">{fmt(totalAll)} THB</div>
            </div>

            {/* เลือกวิธีชำระ */}
            <div className="co-pay-methods">
              <button className={`co-pay-method${payMethod === "cash" ? " active" : ""}`} onClick={() => setPayMethod("cash")}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/>
                  <path d="M6 12h.01M18 12h.01"/>
                </svg>
                {t(lang, "coCash")}
              </button>
              <button className={`co-pay-method${payMethod === "qr" ? " active" : ""}`} onClick={() => setPayMethod("qr")}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <path d="M14 14h3v3h-3zM21 14v.01M14 21v.01M21 21v.01M17.5 17.5v.01"/>
                </svg>
                QR Code
              </button>
            </div>

            {/* PromptPay QR — ยอดเงินล็อคใน QR */}
            {payMethod === "qr" && (
              <div className="co-pay-qrbox">
                <div style={{ background:"#fff", padding:10, borderRadius:14, border:"1px solid #e5e7eb" }}>
                  <PromptPayQR amount={totalAll} size={180} />
                </div>
                <div style={{ fontWeight:600, color:"#1a1a1a", fontSize:13 }}>
                  {t(lang,"coScanPay")} {fmt(totalAll)} THB
                </div>
                <div style={{ textAlign:"center", lineHeight:1.6 }}>
                  {promptpayConfig.accountName} · {promptpayConfig.bankName}<br/>
                  PromptPay {promptpayConfig.phoneNumber}
                </div>
              </div>
            )}

            <div className="co-pay-actions">
              <button className="co-pay-cancel" onClick={() => setShowPay(false)}>{t(lang, "coCancelBtn")}</button>
              <button className="co-pay-ok" onClick={confirmPay}>{t(lang, "coOkBtn")}</button>
            </div>
          </div>
        </div>
      )}

      <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />
    </>
  );
}

export default CheckOut;